import { useState } from "react";
import axios, { AxiosProgressEvent } from "axios";
import { v4 as uuid } from "uuid";
import { useLazyGetBrandComplianceUploadPolicyQuery } from "services/api/api.slice";
import { useDispatch } from "./redux";
import { setNotification } from "services";
import { BrandComplianceUploadedFileProps } from "../interfaces/BrandCompliance";

// Uploads brand-compliance submission files to the BrandComplianceBucket using the
// S3 POST policy from GET /brand-compliance/upload-policy. Each policy fetch returns
// a fresh uploadDirectory ({uploadFolder}/{policy-guid}); the policy only permits keys
// under that directory, so the full object key each file lands under is
// `${uploadDirectory}/${packageId}/${fileName}` and is returned to the caller via
// handleSuccess (as fileKey) for use in the submit payload's files[].fileKey.
// Files are stored indefinitely — there is intentionally no delete endpoint.
const useS3BrandComplianceUpload = () => {
  const dispatch = useDispatch();

  const [callGetUploadPolicy, { isFetching }] = useLazyGetBrandComplianceUploadPolicyQuery();
  const [loading, _setLoading] = useState<boolean>(false);
  const onSuccess = async (
    awsPolicy: any,
    newFile: File | null,
    handleSuccess: (response: BrandComplianceUploadedFileProps) => void,
    packageId: string,
    progressCallback?: Function,
    handleFailure?: (file: File) => void
  ) => {
    if (newFile) {
      const fileExtension = newFile.name.split(".").pop();
      const fileUuid = uuid(),
        extension = fileExtension ? fileExtension.toLowerCase() : "png",
        fileName = newFile.name,
        mimeType = newFile.type,
        size = newFile.size,
        bodyFormData = new FormData();

      const uploadLocation = `${awsPolicy.uploadDirectory}/${packageId}/${newFile.name}`;

      bodyFormData.append("key", uploadLocation);
      bodyFormData.append("AWSAccessKeyId", awsPolicy.accessKey);
      bodyFormData.append("acl", "private");
      bodyFormData.append("success_action_status", "201");
      bodyFormData.append("policy", awsPolicy.policy);
      bodyFormData.append("signature", awsPolicy.signature);
      bodyFormData.append("Content-Type", newFile.type);
      bodyFormData.append(
        "Content-Disposition",
        `attachment; filename=${newFile.name}`
      );
      bodyFormData.append("file", newFile);
      await axios({
        method: "post",
        url: `https://${awsPolicy.bucket}.s3.amazonaws.com/`,
        data: bodyFormData,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          progressCallback && progressCallback(newFile.name, progressEvent);
        },
      })
        .then(() => {
          const response: BrandComplianceUploadedFileProps = {
            extension,
            name: fileName,
            fileId: fileUuid,
            mimeType,
            sizeInBytes: size,
            packageId: packageId,
            fileKey: uploadLocation,
          };

          _setLoading(false);
          dispatch(
            setNotification({
              type: "success",
              message: "File has been uploaded. Submit still required.",
            })
          );
          handleSuccess(response);
        })
        .catch((err: any) => handleError(err, newFile, handleFailure));
    }
  };
  const handleError = (
    _err: any,
    file?: File | null,
    handleFailure?: (file: File) => void
  ) => {
    dispatch(
      setNotification({
        type: "error",
        message: "An error has occurred, please try again.",
      })
    );
    _setLoading(false);
    if (file && handleFailure) {
      handleFailure(file);
    }
  };
  const uploadS3 = async (
    newFile: File | null,
    handleSuccess: (response: BrandComplianceUploadedFileProps) => void,
    packageId: string,
    progressCallback?: Function,
    handleFailure?: (file: File) => void
  ) => {
    _setLoading(true);
    await callGetUploadPolicy()
      .unwrap()
      .then((awsPolicy: any) =>
        onSuccess(
          awsPolicy,
          newFile,
          handleSuccess,
          packageId,
          progressCallback,
          handleFailure
        )
      )
      .catch((err: any) => handleError(err, newFile, handleFailure));
  };

  return { uploadS3, loading: loading || isFetching };
};

export default useS3BrandComplianceUpload;
